import { useEffect, useMemo, useState } from 'react';
import pick from 'lodash/pick';
import keyBy from 'lodash/keyBy';
import { Obligation } from '@common/types/obligations';
import { useCheckTaxRatesToChangeAfterObligationUpdate } from '@mutations/obligations';

const getObligationId = ({ companyCode, countryCode, countryTaxType }: Obligation) => `${companyCode}_${countryTaxType}_${countryCode}`;

const initialState = { taxRatesToUpdate: 0, unapprovedEntryCount: 0 };

export const useRateChangesToUpdateAfterImportCount = ({
  uploadedEntities,
  existingEntities,
}: {
  uploadedEntities: Obligation[],
  existingEntities: Obligation[],
}) => {
  const [
    { taxRatesToUpdate, unapprovedEntryCount },
    setTaxRatesToUpdate,
  ] = useState(initialState);
  const existingEntitiesByKey = useMemo(() => keyBy(existingEntities, getObligationId), [existingEntities]);

  const {
    checkTaxRatesToUpdate,
    isLoading: isLoadingRateChangesToUpdate,
  } = useCheckTaxRatesToChangeAfterObligationUpdate();

  const obligationsWithUpdatedDefTaxRate = useMemo(() =>
    uploadedEntities.filter(uploadedEntity =>
      existingEntitiesByKey[getObligationId(uploadedEntity)]?.deferredTaxRate !== uploadedEntity.deferredTaxRate
    ).map(obligation => pick(obligation, ['companyCode', 'countryCode', 'countryTaxType', 'deferredTaxRate']))
  , [uploadedEntities, existingEntitiesByKey]);

  useEffect(() => {
    (async () => {
      if (obligationsWithUpdatedDefTaxRate.length) {
        const taxRatesToUpdate = await checkTaxRatesToUpdate(obligationsWithUpdatedDefTaxRate);
        setTaxRatesToUpdate(taxRatesToUpdate);
      } else {
        setTaxRatesToUpdate(initialState);
      }
    })();
  }, [obligationsWithUpdatedDefTaxRate, checkTaxRatesToUpdate]);

  return { taxRatesToUpdate, unapprovedEntryCount, isLoadingRateChangesToUpdate };
};
